import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { collection, doc, runTransaction, serverTimestamp } from "firebase/firestore";
import toast from "react-hot-toast";
import { db } from "../../firebase";
import { generateRoomCode } from "./omiUtils";

export default function OmiCreateRoomModal({
  open,
  onClose,
  user,
  setCurrentRoomId,
}) {
  const [betAmount, setBetAmount] = useState("1");
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    if (loading) return;
    setBetAmount("1");
    onClose();
  };

  const handleCreateRoom = async () => {
    if (!user?.uid) {
      sessionStorage.setItem("postLoginRedirect", "/omi");
      toast.error("Please log in to create an Omi table.");
      return;
    }

    const amount = Number(betAmount || 0);
    if (amount < 1) {
      toast.error("Minimum entry fee is 1 USDT.");
      return;
    }

    setLoading(true);
    try {
      const roomRef = doc(collection(db, "omi_rooms"));
      const roomCode = generateRoomCode();

      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await transaction.get(userRef);
        if (!userSnap.exists()) throw new Error("User not found.");

        const balance = Number(userSnap.data()?.balance || 0);
        if (balance < amount) throw new Error("Insufficient balance.");

        transaction.update(userRef, {
          balance: Number((balance - amount).toFixed(2)),
          activeOmiRoomId: roomRef.id,
        });

        transaction.set(roomRef, {
          roomId: roomCode,
          createdBy: {
            userId: user.uid,
            username: user.displayName || user.email || "Player",
            avatar: user.photoURL || "",
          },
          createdAt: serverTimestamp(),
          status: "waiting",
          betAmount: amount,
          players: [
            {
              userId: user.uid,
              username: user.displayName || user.email || "Player",
              avatar: user.photoURL || "",
              seat: 1,
              joinedAt: new Date(),
            },
          ],
          maxPlayers: 4,
          turnOrder: [],
          turnUserId: null,
          hands: {},
          trick: [],
          lastTrick: [],
          scores: { teamA: 0, teamB: 0 },
          trump: null,
          round: 0,
          trickNumber: 0,
          winnerTeam: null,
          payoutProcessed: false,
          rematchReady: [],
          finishedReason: null,
          forfeitedBy: null,
        });
      });

      setCurrentRoomId(roomRef.id);
      toast.success(`Omi table ${roomCode} created.`);
      handleClose();
    } catch (error) {
      toast.error(error?.message || "Failed to create Omi table.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="w-full max-w-md rounded-[28px] border border-emerald-400/30 bg-[#131923] p-6 shadow-[0_30px_90px_rgba(0,0,0,0.45)]"
            initial={{ scale: 0.96, y: 18 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96, y: 18 }}
          >
            <div className="text-xs uppercase tracking-[0.24em] text-emerald-300">
              Omi
            </div>
            <h3 className="mt-2 text-3xl font-black text-white">Create Table</h3>
            <p className="mt-3 text-sm leading-7 text-slate-400">
              Start a four-seat Omi table, set the entry fee, and share the room code
              with your friends.
            </p>

            <label className="mt-6 block">
              <div className="mb-2 text-xs uppercase tracking-[0.24em] text-slate-500">
                Entry Fee
              </div>
              <input
                type="number"
                min="1"
                step="0.5"
                value={betAmount}
                onChange={(event) => setBetAmount(event.target.value)}
                className="w-full rounded-[18px] bg-[#1c2330] px-4 py-4 text-lg font-bold text-white outline-none"
              />
            </label>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="rounded-[18px] border border-white/10 bg-white/[0.04] px-4 py-3 font-semibold text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateRoom}
                disabled={loading}
                className="rounded-[18px] bg-emerald-400 px-4 py-3 font-black text-[#05120d] disabled:opacity-60"
              >
                {loading ? "Creating..." : "Create Table"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
